import ApiService from '@/data/services/ApiService'
import { ICoach, ICoachCreateForm } from '@/data/models/Coach'
import { IPaginationQueryParams } from '@/data/models/Pagination'
import { AxiosResponse, AxiosRequestConfig } from 'axios'
import { IPaginatedServiceResponse, IServiceResponse } from '@/data/models/ServiceResponse'

export interface ICoachGetPaginatedQuery extends IPaginationQueryParams {
  teamId?: number;
}

export class CoachApiService extends ApiService {
  /**
   * Returns paginated list of Coachs
   *
   * @param query
   */
  static async getPaginated(query: ICoachGetPaginatedQuery): Promise<IPaginatedServiceResponse<ICoach>> {
    return this.axios.get('coaches', { params: query })
      .then((response: AxiosResponse<IPaginatedServiceResponse<ICoach>>) => response.data)
  }

  /**
   * Given coach's id, fetches coach data from database
   *
   * @param id
   */
  static async getCoachDetails(id: string): Promise<IServiceResponse<ICoach>> {
    return this.axios.get(`coaches/${id}`)
      .then((response: AxiosResponse) => response.data)
  }

  /**
   * Given data, creates new entry in database
   *
   * @param coachFormData
   */
  static async create(coachFormData: ICoachCreateForm): Promise<IServiceResponse<ICoach>> {
    return this.axios.post('coaches', coachFormData)
      .then((response: AxiosResponse<IServiceResponse<ICoach>>) => response.data)
  }

  /**
   * Given data, updates coach's entry in database
   *
   * @param data
   */
  static async update(data: ICoachCreateForm): Promise<IServiceResponse<ICoach>> {
    return this.axios.put(`coaches/${data.id}`, data)
      .then((response: AxiosResponse<IServiceResponse<ICoach>>) => response.data)
  }

  /**
   * Given data, edit coach's entry in database from user interface
   *
   * @param data
   */
  static async editProfile(data: ICoachCreateForm): Promise<IServiceResponse<ICoach>> {
    return this.axios.patch('coaches/edit-profile', data)
      .then((response: AxiosResponse<IServiceResponse<ICoach>>) => response.data)
  }

  /**
   * Given coach's id, deletes event in database
   *
   * @param coachId
   */
  static async delete(coachId: string): Promise<AxiosResponse> {
    return this.axios.delete(`coaches/${coachId}`)
  }

  /**
   * Get coach-specific filters for a given type
   *
   * @param type
   */
  static async getFilters(type: string): Promise<any> {
    return this.axios.get(`coach/filters/${type}`)
      .then((response: AxiosResponse<any>) => response.data)
  }

  /**
   * Update coach-specific filters for a given type
   *
   * @param type
   * @param filters
   */
  static async updateFilters(type: string, filters: object): Promise<any> {
    return this.axios.post(`coach/filters/${type}`, { filters })
      .then((response: AxiosResponse<any>) => response.data)
  }
}
